import { Pagination, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useCallback, useEffect, useReducer, useState } from "react";
import Styles from "@/assets/styles/Styles";
import type { CustomButton } from "@/components/DataList/common/DataListOptions";
import type { DataListHeaderProps } from "@/components/DataList/ListHeader/DataListHeader";
import DataListHeader from "@/components/DataList/ListHeader/DataListHeader";
import type { DataListRow, DataListRowInternal } from "@/components/DataList/DataListRow";
import GridView from "@/components/DataList/ViewTypes/GridView";
import type { DataListViewProps } from "@/components/DataList/ViewTypes/ListView";
import ListView from "@/components/DataList/ViewTypes/ListView";
import type { KioPaginatorProps } from "@/components/KioPaginator";
import type { PaginationResult } from "@/declarations/PaginationResult";
import Loader from "@/framework/Loader";
import { useDepSafeValue } from "@/hooks/useDepSafeCallback";
import { useLoadingState } from "@/hooks/useLoadingState";
import { ViewType } from "@/components/DataList/ListHeader/ViewTypeSelector";
import Settings from "@/Settings";
import { resolvePotentiallyLocalizedString } from "@/utils/obj";
import { cancellablePromise } from "@/utils/async";
import { usePrevious } from "@/hooks/usePrevious";
import type { ExpandedDmMedia } from "@/declarations/models/ExpandedDmMedia";

export interface CommonDataListProps {
  onTagClicked?: (tag: string) => void;
  altUpdatedAt?: boolean;
  fallbackImage?: string;
  hideUpdated?: boolean;
  hideImage?: boolean;
  denseOptions?: boolean;
  customListItemButtons?: Array<CustomButton>;
  expandedContent?: Array<ExpandedDmMedia>;
  canExpandContent?: boolean;
}

export interface DataListProps<T>
  extends CommonDataListProps,
    Pick<KioPaginatorProps, "pageSizes" | "defaultPageSize" | "showFirstLastButtons" | "showPreviousNextButtons">,
    Pick<
      DataListHeaderProps,
      | "disableSorting"
      | "disableViewTypeSelection"
      | "onToggle"
      | "toggleLabel"
      | "toggleDefault"
      | "listTitle"
      | "sortOptions"
      | "initialSortOption"
      | "filterOptions"
      | "filter"
      | "createNewButton"
      | "searchProp"
      | "customBatchButtons"
      | "customToggles"
    > {
  children?: ReactNode;
  /**
   * A mapper-function to map a generic item to a common ListItemData-object
   * @param item
   */
  mapperFn: (item: T) => DataListRow;
  /**
   * This function should generate a promise that resolves an Array of items to display,
   * based on the provided page and page number.
   * The actual "fetching" will be handled by this List-component.
   * @param page The page to fetch
   * @param pageSize the number of items to fetch
   */
  getItems: (page: number, pageSize: number) => Promise<PaginationResult<T>>;
  onSelectionChanged?: (selectedItems: Array<T>) => void;
  onItemClick?: (item: T) => void;
  onEditItem?: (item: T) => void;
  /**
   * Deleting an item is usually an async operation.
   * Therefore, if the returned promise is resolved,
   * it is assumed that the delete-operation was successful, and the "getItems(...)" callback is called again to refresh the page.
   * If it is rejected, it is assumed that the delete-operation failed, and no action will be taken.
   * @param item
   */
  onDeleteItem?: (item: T) => Promise<void>;
  onDeleteItemForever?: (item: T) => Promise<void>;
  onUndeleteItem?: (item: T) => Promise<void>;
  handleOnItemsChanged?: (sortProp?: string, sortDirection?: string, filter?: string) => void;
  selectedItems?: string[];
  disablePagination?: boolean;
  externalDataChanged?: object | number;
  resetPageDeps?: any[];
  disableSelection?: boolean;
  defaultViewType?: ViewType;
  selectedLanguage?: string;
  isInsideModal?: boolean; // Deactivates searchparams and uses fallbacks
  hideHeader?: boolean;
  expandedContent?: Array<ExpandedDmMedia>;
  canExpandContent?: boolean;
}

const Container = styled("div")({
  display: "flex",
  flexFlow: "column",
  overflow: "hidden",
  height: "100%",
  width: "100%",
});

const ItemsContainer = styled("div")({
  height: "100%",
  overflow: "hidden auto",
});

const NoContentText = styled(Typography)({
  textAlign: "center",
  display: "block",
});

export const PaginatorContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2, 0),
  borderTop: `1px solid ${theme.palette.divider}`,
  "& .MuiPaginationItem-page.Mui-selected": {
    background: Styles.Colors.PAGINATION_SELECTED_BACKGROUND_COLOR,
  },
}));

const DEFAULT_PAGE_SIZES = [5, 10, 25, 50, 100];
const DEFAULT_RESET_PAGE_DEPS: any[] = [];

export const DataList = <T extends object>({
  children,
  listTitle,
  getItems,
  mapperFn,
  selectedItems,
  onSelectionChanged,
  onItemClick,
  onEditItem,
  onDeleteItem,
  onDeleteItemForever,
  onUndeleteItem,
  onToggle,
  handleOnItemsChanged,
  onTagClicked,
  disablePagination = false,
  disableSorting = false,
  disableViewTypeSelection = false,
  pageSizes = DEFAULT_PAGE_SIZES,
  defaultPageSize = 10,
  showFirstLastButtons = false,
  showPreviousNextButtons = true,
  toggleLabel = "",
  toggleDefault = false,
  hideUpdated = false,
  hideImage = false,
  altUpdatedAt = false,
  denseOptions = false,
  externalDataChanged,
  disableSelection,
  defaultViewType = ViewType.LIST,
  fallbackImage,
  selectedLanguage = Settings.DEFAULT_LOCALE,
  sortOptions,
  initialSortOption,
  filterOptions,
  filter,
  createNewButton,
  customListItemButtons,
  resetPageDeps = DEFAULT_RESET_PAGE_DEPS,
  searchProp,
  customBatchButtons,
  isInsideModal = false,
  hideHeader = false,
  expandedContent,
  canExpandContent = false,
  customToggles,
}: DataListProps<T>) => {
  const { t } = useTranslation("common");
  const getPotentiallyLocalizedString = resolvePotentiallyLocalizedString(selectedLanguage);

  const onSelectionChangedRef = useDepSafeValue(onSelectionChanged);
  const getItemsRef = useDepSafeValue(getItems);
  const mapperRef = useDepSafeValue((item: T): DataListRowInternal<T> => {
    const dataRow: DataListRow = mapperFn(item);
    return {
      ...dataRow,
      title: getPotentiallyLocalizedString(dataRow.title) ?? "",
      subTitle: getPotentiallyLocalizedString(dataRow.subTitle) ?? "",
      sourceItem: item,
    };
  });

  const { isLoading, startLoading, stopLoading } = useLoadingState();
  const [isGridView, setIsGridView] = useState<boolean>(defaultViewType === ViewType.GRID);

  const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>(0);
  const [items, setItems] = useState<Array<DataListRowInternal<T>>>([]);
  const paginationDisabled = disablePagination || totalNumberOfItems <= defaultPageSize;
  const [selectionState, setSelectionState] = useReducer(
    (state: { [id: string]: T | null }, [id, selected]: [id: string, selected: T | null]) => ({
      ...state,
      [id]: selected,
    }),
    {}
  );

  const [currentPageIfModal, setCurrentPageIfModal] = useState(0);
  const [currentPageSizeIfModal, setCurrentPageSizeIfModal] = useState(defaultPageSize);

  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = isInsideModal ? currentPageIfModal : Number(searchParams.get("currentPage") || 0);
  const currentPageSize = isInsideModal
    ? currentPageSizeIfModal
    : Number(searchParams.get("currentPageSize") || defaultPageSize);

  const prevFilter = usePrevious(filter, filter);
  const prevResetPageDeps = usePrevious(resetPageDeps, resetPageDeps);

  const arrayEquals = (firstArray: any[], secondArray: any[]) =>
    firstArray.length === secondArray.length && firstArray.every((element, index) => element === secondArray[index]);

  // Go back to page 1 (0) when various states are changed
  useEffect(() => {
    if ((filter !== prevFilter || !arrayEquals(resetPageDeps, prevResetPageDeps || [])) && currentPage !== 0) {
      if (!isInsideModal)
        setSearchParams((params) => {
          params.set("currentPage", String(0));
          return params;
        });
      else {
        setCurrentPageIfModal(0);
      }
    }
  }, [currentPage, filter, prevFilter, prevResetPageDeps, resetPageDeps, setSearchParams]);

  const handlePageChange: KioPaginatorProps["onPageChange"] = (page, pageSize) => {
    const lastPageIndex = totalNumberOfItems > 0 ? Math.ceil(totalNumberOfItems / pageSize) - 1 : 0;
    const newCurrentPage = page >= lastPageIndex ? lastPageIndex : page;
    if (!isInsideModal) {
      setSearchParams((params) => {
        params.set("currentPage", String(newCurrentPage));
        params.set("currentPageSize", String(pageSize));
        return params;
      });
    } else {
      setCurrentPageIfModal(newCurrentPage);
      setCurrentPageSizeIfModal(pageSize);
    }
  };

  const handleSelectionChanged = (selected: boolean, key: string, item: T) => {
    setSelectionState([key, selected ? item : null]);
  };

  const reloadPage = useCallback(
    async (pageNumber: number, pageSize: number) => {
      if (!getItemsRef.current) {
        setItems([]);
        return;
      }
      try {
        startLoading();
        const paginationResult = await getItemsRef.current(pageNumber, pageSize);
        setTotalNumberOfItems(paginationResult?.total_count || 0);
        const items = paginationResult?.items || [];
        setItems(items.map((i) => ({ ...mapperRef.current(i), sourceItem: i })));
      } catch (e) {
        console.error(e);
        setItems([]);
      } finally {
        stopLoading();
      }
    },
    [getItemsRef, mapperRef, startLoading, stopLoading]
  );

  const handleDeleteItem = async (item: DataListRowInternal<T>): Promise<void> => {
    if (!!onDeleteItemForever || window.confirm(t("components.list.confirmDelete"))) {
      try {
        await onDeleteItem?.(item.sourceItem);
        await reloadPage(currentPage, currentPageSize);
      } catch (e) {
        console.warn("Delete item failed:", item.sourceItem, e);
      }
    }
  };

  const handleDeleteItemForever = async (item: DataListRowInternal<T>): Promise<void> => {
    if (window.confirm(t("components.list.confirmDelete"))) {
      try {
        await onDeleteItemForever?.(item.sourceItem);
        await reloadPage(currentPage, currentPageSize);
      } catch (e) {
        console.warn("Delete item forever failed:", item.sourceItem, e);
      }
    }
  };

  const handleUndeleteItem = async (item: DataListRowInternal<T>): Promise<void> => {
    try {
      await onUndeleteItem?.(item.sourceItem);
      await reloadPage(currentPage, currentPageSize);
    } catch (e) {
      console.warn("Delete item forever failed:", item.sourceItem, e);
    }
  };

  useEffect(() => {
    // Load items on init and on page-change/page-size-change/external data change
    const [fetchItems, cancel] = cancellablePromise(reloadPage(currentPage, currentPageSize));
    fetchItems.then();
    return cancel;
  }, [currentPage, currentPageSize, reloadPage, externalDataChanged]);

  useEffect(() => {
    if (!onSelectionChangedRef.current) return;

    onSelectionChangedRef.current(
      Object.keys(selectionState)
        .filter((key) => selectionState[key] !== null)
        .map((key) => selectionState[key] as T)
    );
  }, [selectionState, onSelectionChangedRef]);

  const enableSelection: boolean =
    !disableSelection && (!!onSelectionChanged || !!onDeleteItem || !!onUndeleteItem || !!customBatchButtons?.length);
  const treatItemClickAsSelect: boolean = !onItemClick && enableSelection;
  const itemClickIsDisabled: boolean = !treatItemClickAsSelect && !onItemClick;
  const handleOnItemClick = (item: DataListRowInternal<T>) => {
    return treatItemClickAsSelect || ((item?.pictureCount ?? 0) <= 1 && enableSelection && canExpandContent)
      ? handleSelectionChanged(!selectionState[item.key], item.key, item.sourceItem)
      : onItemClick?.(item.sourceItem);
  };

  const expandedContentItems = expandedContent?.flatMap((content) => content.items) ?? [];
  const dataListHeaderItems = [
    ...items.filter(
      (item) =>
        !(expandedContent ?? []).some(
          (content) =>
            String(content.parentItemId) === String(item.key) &&
            content.items.some((expandedItem) => String(expandedItem.key) === String(item.key))
        )
    ),
    ...(expandedContentItems.map((img) => {
      return {
        key: img.key,
        title: img.title,
        imageURL: img.imageURL,
        sourceItem: img,
      };
    }) as Array<DataListRowInternal<T>>),
  ];

  const commonProps: DataListViewProps<T> = {
    items,
    getIsSelected: (item) => !!selectionState[item.key],
    onSelectChanged: (item, selected) => handleSelectionChanged(selected, item.key, item.sourceItem),
    onItemClick: !itemClickIsDisabled ? handleOnItemClick : undefined,
    onEdit: onEditItem ? (item) => onEditItem(item.sourceItem) : undefined,
    onDelete: onDeleteItem ? handleDeleteItem : undefined,
    onDeleteForever: onDeleteItemForever ? handleDeleteItemForever : undefined,
    onUndelete: onUndeleteItem ? handleUndeleteItem : undefined,
    onTagClicked,
    selectedItems,
    enableSelection,
    hideUpdated,
    altUpdatedAt,
    denseOptions,
    hideImage,
    fallbackImage,
    customListItemButtons,
    expandedContent,
    canExpandContent,
  };

  return (
    <Container>
      {children}

      {!hideHeader && (
        <DataListHeader
          items={dataListHeaderItems}
          listTitle={listTitle}
          onItemsChanged={handleOnItemsChanged}
          disableSorting={disableSorting}
          disablePagination={paginationDisabled}
          enableSelection={enableSelection}
          selectedViewType={isGridView ? ViewType.GRID : ViewType.LIST}
          setSelectedViewType={() => setIsGridView(!isGridView)}
          disableViewTypeSelection={disableViewTypeSelection}
          onToggle={onToggle}
          toggleLabel={toggleLabel}
          toggleDefault={toggleDefault}
          page={currentPage}
          pageSize={currentPageSize}
          availablePageSizes={pageSizes}
          totalCount={totalNumberOfItems}
          onPageChange={handlePageChange}
          onToggleSelection={(selectAll) =>
            dataListHeaderItems
              .filter((item) => !selectedItems?.includes(item.key))
              .forEach((item) => handleSelectionChanged(selectAll, item.key, item.sourceItem))
          }
          sortOptions={sortOptions}
          initialSortOption={initialSortOption}
          filterOptions={filterOptions}
          filter={filter}
          createNewButton={createNewButton}
          searchProp={searchProp}
          selectedKeys={dataListHeaderItems
            .filter((item) => Object.entries(selectionState).some(([key, value]) => item.key === key && !!value))
            .map((item) => item.key)}
          onDeleteItem={onDeleteItem}
          onUndeleteItem={onUndeleteItem}
          customBatchButtons={customBatchButtons}
          customToggles={customToggles}
        />
      )}

      <ItemsContainer>
        {isLoading ? (
          <Loader loadingText="components.list.loadingContent" />
        ) : !items?.length ? (
          <NoContentText variant="overline" color="textSecondary">
            {t("components.list.noContent")}
          </NoContentText>
        ) : (
          <>
            {isGridView && <GridView {...commonProps} />}
            {!isGridView && <ListView {...commonProps} />}
          </>
        )}
      </ItemsContainer>

      {!paginationDisabled && (
        <PaginatorContainer>
          <Pagination
            page={currentPage + 1}
            count={Math.ceil(totalNumberOfItems / currentPageSize) || 0}
            onChange={(_: any, newPage: number) => handlePageChange(newPage - 1, currentPageSize)}
            showFirstButton={showFirstLastButtons}
            showLastButton={showFirstLastButtons}
            hideNextButton={!showPreviousNextButtons}
            hidePrevButton={!showPreviousNextButtons}
            size="large"
          />
        </PaginatorContainer>
      )}
    </Container>
  );
};

export default DataList;
